import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { LUNAR } from '../constants/testIds';
import { Shield, Award, Landmark, Mail, CheckCircle, ArrowRight, CalendarClock, Trophy, Ship } from 'lucide-react';
import { toast } from 'sonner';
import { useArtifacts } from '../hooks/useArtifacts';
import { CatalogCard } from '../components/ArtifactCards';
import { Footer } from '../components/Footer';
import { Seo } from '../components/Seo';
import { useSiteContent } from '../context/SiteContentContext';

// Renders hero subtitle text unchanged, but turns any URL and the platform names
// "Invaluable" / "AuctionZip" into clickable gold links pointing to the current auction.
const HERO_LINK_CLASS = 'text-luxury-gold underline decoration-luxury-gold/40 hover:decoration-luxury-gold underline-offset-2 transition-colors break-words';
function renderHeroSubtitle(text) {
  if (!text) return null;
  const invMatch = text.match(/https?:\/\/[^\s]*invaluable\.com[^\s]*/i);
  const azMatch = text.match(/https?:\/\/[^\s]*auctionzip\.com[^\s]*/i);
  const invUrl = invMatch ? invMatch[0] : null;
  const azUrl = azMatch ? azMatch[0] : null;
  const parts = text.split(/(https?:\/\/[^\s]+|\bInvaluable\b|\bAuctionZip\b)/g);
  return parts.map((part, i) => {
    if (!part) return null;
    if (/^https?:\/\//i.test(part)) {
      return <a key={i} href={part} target="_blank" rel="noopener noreferrer" className={HERO_LINK_CLASS} data-testid={`hero-link-url-${i}`}>{part}</a>;
    }
    if (part === 'Invaluable' && invUrl) {
      return <a key={i} href={invUrl} target="_blank" rel="noopener noreferrer" className={HERO_LINK_CLASS} data-testid="hero-link-invaluable">{part}</a>;
    }
    if (part === 'AuctionZip' && azUrl) {
      return <a key={i} href={azUrl} target="_blank" rel="noopener noreferrer" className={HERO_LINK_CLASS} data-testid="hero-link-auctionzip">{part}</a>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

export default function Home() {
  const location = useLocation();
  const { artifacts, loading } = useArtifacts();
  const { content: c } = useSiteContent();

  // Contact form state
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);

  // Collector list signup state
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupSubmitting, setSignupSubmitting] = useState(false);
  const [signupDone, setSignupDone] = useState(false);

  // Scroll to a section when arriving via a hash link (e.g. /#contact from another page).
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace('#', '');
    const doScroll = () => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    const t1 = setTimeout(doScroll, 200);
    const t2 = setTimeout(doScroll, 800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [location.hash, loading]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactName || !contactEmail || !contactMessage) return;
    setContactSubmitting(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        name: contactName,
        email: contactEmail,
        message: contactMessage,
      });
      setContactSubmitted(true);
      toast.success('Message Sent', {
        description: "Thanks for reaching out. We'll get back to you within 24 hours."
      });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Unable to send your message. Please try again.';
      toast.error('Message Failed', { description: msg });
    } finally {
      setContactSubmitting(false);
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    if (!signupEmail) return;
    setSignupSubmitting(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/signup`, {
        email: signupEmail,
        name: signupName,
        signup_type: 'collector'
      });
      setSignupDone(true);
      setSignupName('');
      setSignupEmail('');
      // Gently bring them back to the top of the homepage after a few seconds.
      setTimeout(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, 6000);
      toast.success("You're on the list", {
        description: "Thanks for signing up. We'll email you about upcoming auctions and featured artifacts."
      });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Unable to complete signup. Please try again.';
      toast.error('Signup Failed', { description: msg });
    } finally {
      setSignupSubmitting(false);
    }
  };

  // Preview: prefer Featured (live) pieces, fall back to Upcoming so it never looks empty.
  const live = artifacts.filter((a) => a.status === 'live');
  const upcoming = artifacts.filter((a) => a.status === 'upcoming');
  const previewSource = live.length ? { items: live, to: '/upcoming', label: 'Auction Preview' }
    : { items: upcoming, to: '/upcoming', label: 'From the Upcoming Auction' };
  const previewItems = previewSource.items.slice(0, 5);

  const routeCards = [
    { icon: CalendarClock, label: 'View Auction Preview', to: '/upcoming', type: 'route', testId: 'home-cta-upcoming' },
    { icon: Trophy, label: 'View Past Auction Results', to: '/past', type: 'route', testId: 'home-cta-past' },
    { icon: Ship, label: 'Consign Your Collection', to: '/consign', type: 'route', testId: 'home-cta-consign' },
    { icon: Mail, label: 'Join Collector List', to: 'mailing-list', type: 'scroll', testId: 'home-cta-collector' },
  ];

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-luxury-black text-white">
      <Seo
        path="/"
        title="Lunar Legacies | Space Memorabilia Auctions & Consignments"
        description="Trusted space memorabilia auction house founded in 1998. Authentic Apollo, Gemini, Mercury, Skylab and Space Shuttle artifacts, astronaut autographs, flown items, hardware and documents — dedicated auctions since 2009."
      />
      {/* Hero Section */}
      <section className="relative min-h-[88vh] flex items-center justify-center bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1614729375290-b2a429db839b?auto=format&fit=crop&q=80')` }}>
        <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/75 to-luxury-black"></div>
        <div className="absolute inset-0 bg-black/25"></div>

        <div className="relative z-10 max-w-4xl mx-auto text-center px-6 py-24">
          <span className="text-[10px] sm:text-xs uppercase tracking-[0.4em] text-luxury-gold font-semibold mb-5 block hero-sub" data-testid={LUNAR.heroSubtitle}>
            {c.hero_eyebrow}
          </span>
          <h1 className={`font-serif-luxury tracking-tight leading-[1.08] mb-6 hero-title whitespace-pre-line ${({small:'text-2xl sm:text-3xl lg:text-4xl',medium:'text-4xl sm:text-5xl lg:text-6xl',large:'text-5xl sm:text-6xl lg:text-7xl',xlarge:'text-6xl sm:text-7xl lg:text-8xl'})[c.hero_headline_size] || 'text-4xl sm:text-5xl lg:text-6xl'}`} style={{ color: c.hero_headline_color || '#FFFFFF' }} data-testid={LUNAR.heroTitle}>
            {c.hero_headline}
          </h1>
          <p className="max-w-xl mx-auto text-sm sm:text-base text-white/85 font-sans-luxury tracking-wide leading-relaxed mb-10 hero-sub whitespace-pre-line">
            {renderHeroSubtitle(c.hero_subtitle)}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/consign" className="btn-gold w-full sm:w-auto font-sans-luxury uppercase tracking-[0.2em] text-xs font-semibold px-9 py-4 rounded-none" data-testid={LUNAR.heroCtaConsign}>
              Consign Memorabilia
            </Link>
            <Link to="/upcoming" className="btn-ghost-gold w-full sm:w-auto font-sans-luxury uppercase tracking-[0.2em] text-xs px-9 py-4 rounded-none" data-testid={LUNAR.heroCtaAuctions}>
              View Auction Preview
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Banner */}
      <section className="bg-luxury-charcoal py-8 border-y border-white/5 px-6 md:px-12">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">
          <div className="flex items-center space-x-4 justify-center md:justify-start">
            <div className="p-3 bg-black border border-luxury-gold/20 text-luxury-gold"><Shield size={24} className="stroke-[1.2]" /></div>
            <div>
              <h4 className="font-serif-luxury text-sm tracking-widest uppercase text-white font-bold">{c.trust_1_title}</h4>
              <p className="text-xs text-luxury-muted mt-1 leading-relaxed">{c.trust_1_text}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4 justify-center md:justify-start">
            <div className="p-3 bg-black border border-luxury-gold/20 text-luxury-gold"><Landmark size={24} className="stroke-[1.2]" /></div>
            <div>
              <h4 className="font-serif-luxury text-sm tracking-widest uppercase text-white font-bold">{c.trust_2_title}</h4>
              <p className="text-xs text-luxury-muted mt-1 leading-relaxed">{c.trust_2_text}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4 justify-center md:justify-start">
            <div className="p-3 bg-black border border-luxury-gold/20 text-luxury-gold"><Award size={24} className="stroke-[1.2]" /></div>
            <div>
              <h4 className="font-serif-luxury text-sm tracking-widest uppercase text-white font-bold">{c.trust_3_title}</h4>
              <p className="text-xs text-luxury-muted mt-1 leading-relaxed">{c.trust_3_text}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured preview — a taste */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-24" data-testid="home-featured-preview">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <span className="text-xs uppercase tracking-[0.25em] text-luxury-gold font-bold">A Taste of the Collection</span>
              <h2 className="text-3xl md:text-4xl font-serif-luxury text-white mt-3">{previewSource.label}</h2>
            </div>
            <Link to={previewSource.to} className="text-luxury-gold hover:text-black hover:bg-luxury-gold border-2 border-luxury-gold font-sans-luxury text-base uppercase tracking-[0.2em] font-semibold flex items-center gap-2.5 px-8 py-3.5 transition-all duration-300" data-testid="home-preview-viewall">
              View All <ArrowRight size={18} />
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-16 text-luxury-gold animate-pulse font-serif-luxury tracking-widest">LOADING GALLERY...</div>
          ) : previewItems.length === 0 ? (
            <div className="text-center py-14 text-luxury-muted border border-white/5 bg-luxury-charcoal font-sans-luxury text-sm">
              Our next auction is being finalized. Join the Collector List below to preview it first.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {previewItems.map((art) => (
                <CatalogCard key={art.id} art={art} variant="preview" to={previewSource.to} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Where to go — routing cards */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-luxury-charcoal border-y border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.25em] text-luxury-gold font-bold">Explore</span>
            <h2 className="text-3xl md:text-4xl font-serif-luxury text-white mt-3">Where would you like to go?</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {routeCards.map((card) => {
              const Icon = card.icon;
              const content = (
                <div className="group h-full bg-luxury-black border border-white/10 hover:border-luxury-gold/60 p-7 flex flex-col items-start gap-4 transition-all duration-300">
                  <div className="w-11 h-11 border border-luxury-gold/30 flex items-center justify-center text-luxury-gold group-hover:border-luxury-gold transition-colors duration-300">
                    <Icon size={20} className="stroke-[1.3]" />
                  </div>
                  <span className="text-sm font-serif-luxury text-white group-hover:text-luxury-goldLight transition-colors duration-300 flex-grow">{card.label}</span>
                  <span className="text-luxury-gold flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-sans-luxury font-semibold">
                    Go <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                </div>
              );
              return card.type === 'scroll' ? (
                <button key={card.label} onClick={() => scrollTo(card.to)} className="text-left h-full" data-testid={card.testId}>{content}</button>
              ) : (
                <Link key={card.label} to={card.to} className="h-full" data-testid={card.testId}>{content}</Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-gradient-to-t from-black to-luxury-black border-t border-white/5 relative">
        <div className="absolute inset-0 opacity-10 bg-cover bg-center mix-blend-color-dodge" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1780497651735-d2839c74ea1f?auto=format&fit=crop&q=80')` }}></div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <span className="text-xs uppercase tracking-[0.35em] text-luxury-gold font-bold block mb-4">{c.about_eyebrow}</span>
          <h2 className="text-3xl md:text-5xl font-serif-luxury text-white tracking-tight mb-8">{c.about_heading}</h2>
          <div className="h-0.5 w-24 bg-luxury-gold mx-auto mb-10"></div>
          <p data-testid="home-about-copy" className="text-sm md:text-base text-luxury-muted leading-relaxed font-sans-luxury mb-8 max-w-2xl mx-auto">
            {c.about_paragraph}
          </p>
          <p className="text-xs md:text-sm text-luxury-goldLight/70 italic leading-relaxed font-serif-luxury max-w-xl mx-auto">
            {c.about_quote}
          </p>
          <div className="mt-12 flex justify-center items-center gap-2">
            <span className="h-px w-6 bg-white/20"></span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-luxury-muted font-bold font-sans-luxury">Don Willis, Founder</span>
            <span className="h-px w-6 bg-white/20"></span>
          </div>
        </div>
      </section>

      {/* Custom homepage sections (admin-created) */}
      {Array.isArray(c.custom_blocks) && c.custom_blocks.filter((b) => (b.title || b.body)).length > 0 && (
        <section className="py-20 md:py-28 px-6 md:px-12 lg:px-24 bg-luxury-black border-t border-white/5" data-testid="home-custom-blocks">
          <div className="max-w-3xl mx-auto space-y-16">
            {c.custom_blocks.filter((b) => (b.title || b.body)).map((b, i) => (
              <div key={b.id || i} className="text-center" data-testid={`home-custom-block-${i}`}>
                {b.title && <h2 className="text-2xl md:text-4xl font-serif-luxury text-white mb-5">{b.title}</h2>}
                {b.title && <div className="h-0.5 w-16 bg-luxury-gold mx-auto mb-6"></div>}
                {b.body && <p className="text-sm md:text-base text-luxury-muted leading-relaxed font-sans-luxury whitespace-pre-wrap">{b.body}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Contact */}
      <section id="contact" className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-luxury-charcoal border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.25em] text-luxury-gold font-bold">Contact</span>
            <h2 className="text-2xl md:text-4xl font-serif-luxury text-white mt-2">{c.contact_heading}</h2>
            <p className="text-xs text-luxury-muted mt-3 font-sans-luxury">{c.contact_intro}</p>
          </div>

          <div className="border border-white/5 bg-luxury-black p-8 md:p-12">
            {contactSubmitted ? (
              <div className="text-center py-10">
                <CheckCircle size={40} className="text-luxury-gold mx-auto mb-4" />
                <h3 className="font-serif-luxury text-xl text-white">Message Received</h3>
                <p className="text-xs text-luxury-muted mt-2 font-sans-luxury">Thank you for contacting Lunar Legacies. We{"'"}ll get back to you shortly.</p>
                <button
                  onClick={() => { setContactSubmitted(false); setContactName(''); setContactEmail(''); setContactMessage(''); }}
                  className="mt-6 border border-white/10 text-luxury-gold hover:border-luxury-gold font-sans-luxury text-[10px] uppercase tracking-widest px-6 py-2.5 transition-all duration-300"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-luxury-gold font-sans-luxury mb-2">Name</label>
                    <input type="text" required value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Your name" className="w-full bg-luxury-charcoal border border-white/10 text-white placeholder-white/20 focus:border-luxury-gold text-xs px-4 py-3.5 transition-colors duration-300 outline-none rounded-none" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-luxury-gold font-sans-luxury mb-2">Email</label>
                    <input type="email" required value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="you@email.com" className="w-full bg-luxury-charcoal border border-white/10 text-white placeholder-white/20 focus:border-luxury-gold text-xs px-4 py-3.5 transition-colors duration-300 outline-none rounded-none" data-testid="contact-email-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-luxury-gold font-sans-luxury mb-2">Message</label>
                  <textarea rows={4} required value={contactMessage} onChange={(e) => setContactMessage(e.target.value)} placeholder="Tell us about the item, collection, or question you have." className="w-full bg-luxury-charcoal border border-white/10 text-white placeholder-white/20 focus:border-luxury-gold text-xs px-4 py-3.5 transition-colors duration-300 outline-none rounded-none resize-none" data-testid="contact-message-input"></textarea>
                </div>
                <div className="text-center pt-2">
                  <button type="submit" disabled={contactSubmitting} className="btn-gold w-full font-sans-luxury uppercase tracking-[0.2em] text-xs font-semibold py-4 rounded-none disabled:opacity-50" data-testid="contact-submit-button">{contactSubmitting ? 'Sending…' : 'Send Message'}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Collector List Signup */}
      <section id="mailing-list" className="py-20 md:py-28 px-6 md:px-12 lg:px-24 bg-luxury-black border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="border border-luxury-gold/20 bg-gradient-to-b from-luxury-charcoal to-black p-8 md:p-14 text-center relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-luxury-gold/5 rounded-full blur-3xl"></div>
            <div className="relative z-10">
              <Mail size={26} className="text-luxury-gold mx-auto mb-5 stroke-[1.2]" />
              <span className="text-[10px] sm:text-xs uppercase tracking-[0.35em] text-luxury-gold font-semibold block mb-3">The Collector List</span>
              <h2 className="text-2xl md:text-4xl font-serif-luxury text-white mb-4">{c.collector_heading}</h2>
              <p className="text-xs md:text-sm text-luxury-muted leading-relaxed font-sans-luxury max-w-lg mx-auto mb-8">
                {c.collector_intro}
              </p>

              {signupDone ? (
                <div className="py-6 space-y-3" data-testid="signup-success">
                  <CheckCircle size={34} className="text-luxury-gold mx-auto" />
                  <h3 className="font-serif-luxury text-lg text-white">You{"'"}re on the list</h3>
                  <p className="text-xs text-luxury-muted font-sans-luxury">Thank you. We{"'"}ll be in touch with upcoming auction announcements and featured artifacts.</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center pt-1">
                    <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="btn-gold font-sans-luxury text-[10px] uppercase tracking-[0.2em] font-semibold px-6 py-2.5 rounded-none" data-testid="signup-return-home">
                      Return to Home
                    </button>
                    <button onClick={() => setSignupDone(false)} className="border border-white/15 text-luxury-gold hover:border-luxury-gold font-sans-luxury text-[10px] uppercase tracking-[0.2em] px-6 py-2.5 transition-all duration-300" data-testid="signup-reset-button">
                      Add Another Email
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSignupSubmit} className="max-w-lg mx-auto space-y-4">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input type="text" required value={signupName} onChange={(e) => setSignupName(e.target.value)} placeholder="Name" className="flex-grow bg-black border border-white/10 text-white placeholder-white/25 focus:border-luxury-gold text-xs px-4 py-3.5 outline-none rounded-none transition-colors duration-300" data-testid="signup-name-input" />
                    <input type="email" required value={signupEmail} onChange={(e) => setSignupEmail(e.target.value)} placeholder="Email" className="flex-grow bg-black border border-white/10 text-white placeholder-white/25 focus:border-luxury-gold text-xs px-4 py-3.5 outline-none rounded-none transition-colors duration-300" data-testid="signup-email-input" />
                  </div>
                  <button type="submit" disabled={signupSubmitting} className="btn-gold w-full font-sans-luxury uppercase tracking-[0.2em] text-[10px] font-semibold px-8 py-3.5 rounded-none whitespace-nowrap" data-testid="signup-submit-button">
                    {signupSubmitting ? 'Submitting…' : 'Join Mailing List'}
                  </button>
                  <p className="text-[9px] text-luxury-muted/70 font-sans-luxury tracking-wide">
                    We{"'"}ll only email you about auctions and featured artifacts. Your details are never shared.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
